import { useEffect, useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { readWphg } from '@quirion/api';
import { useBannerContext } from '@quirion/components';
import type { BusinessPartner } from '@quirion/types';

import { getBp } from 'lib/redux/actions/userActions';
import type { State } from 'lib/redux/types';

import { isExpired } from '../updateData/isExpired';

import { isWphgDataValid } from './isWphgDataValid';

/**
 * Checks if WpHG data is valid, complete and not expired.
 */
export const useCheckWphgDataValidity = () => {
  const { notify } = useBannerContext();
  const dispatch = useDispatch();

  const [wphgList, setWphgList] = useState<BusinessPartner[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const businessPartnerList = useSelector(
    (state: State) => state.businessPartner,
  );

  useEffect(() => {
    const bpList = (Object.values(businessPartnerList) as BusinessPartner[]).filter((bp) => bp.provider !== 'BLOXXTER');

    if (!bpList.length) {
      setIsLoading(false);
      return;
    }

    if (!isLoading && !wphgList.length) {
      setIsLoading(true);
      Promise.all(
        bpList.map(async ({ businessPartnerId, wphg }) => {
          if (wphg) {
            setWphgList((prev) => {
              const updatedList = prev ? [...prev] : [];
              updatedList.push({
                ...businessPartnerList[businessPartnerId],
              });
              return updatedList;
            });
          }

          return readWphg({
            payload: {
              businessPartnerId,
              wphgHistory: false,
            },
          }).then((data) => {
            getBp({ data }, businessPartnerId)(dispatch);
            const { wphgHistory, ...rest } = data;
            setWphgList((prev) => {
              const updatedList = prev ? [...prev] : [];
              updatedList.push({
                ...businessPartnerList[businessPartnerId],
                ...rest,
              });
              return updatedList;
            });
          })
        }
        ),
      )
        .then(() => setIsLoading(false))
        .catch((error) => notify({ error }));
    }
  }, [businessPartnerList, wphgList]);

  const bpsWithInvalidWphg = useMemo(() => {
    return isLoading
      ? []
      : wphgList
          .filter(
            (bp) =>
              !bp.wphg ||
              !isWphgDataValid(bp.wphg) ||
              (bp.wphgExpiresAt && isExpired(bp.wphgExpiresAt)),
          ).map((bp) => ({
            ...businessPartnerList[bp.businessPartnerId],
            ...bp,
          }));
  }, [businessPartnerList, isLoading, wphgList]);

  return {
    bpsWithInvalidWphg,
  };
};
